import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Brain, Star, RefreshCw, Loader } from 'lucide-react';
import PersonalityTest from './PersonalityTest';

const PersonalityProfile = () => {
  const [traits, setTraits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTest, setShowTest] = useState(false);

  useEffect(() => {
    loadPersonalityData();
  }, []);

  const loadPersonalityData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/personality/results`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.traits) {
        setTraits(response.data.traits);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // No test taken yet
        setTraits(null);
      } else {
        console.error('Error loading personality data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const traitDescriptions = {
    extroversion: { 
      label: "Extroversion", 
      desc: "Outgoing and energetic", 
      color: "from-blue-500 to-blue-600",
      icon: "💬"
    },
    agreeableness: { 
      label: "Agreeableness", 
      desc: "Friendly and compassionate", 
      color: "from-green-500 to-green-600",
      icon: "❤️"
    },
    conscientiousness: { 
      label: "Conscientiousness", 
      desc: "Organized and responsible", 
      color: "from-purple-500 to-purple-600",
      icon: "📋"
    },
    neuroticism: { 
      label: "Emotional Stability", 
      desc: "Calm and resilient", 
      color: "from-rose-500 to-rose-600",
      icon: "😌",
      inverted: true 
    },
    openness: { 
      label: "Openness", 
      desc: "Creative and curious", 
      color: "from-orange-500 to-orange-600",
      icon: "🎨"
    }
  };

  if (showTest) {
    return (
      <PersonalityTest
        onComplete={(scores) => {
          setTraits(scores);
          setShowTest(false);
        }}
        onCancel={() => setShowTest(false)}
      />
    );
  }

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 border-white/20">
        <CardContent className="flex items-center justify-center py-12">
          <Loader className="h-8 w-8 text-white animate-spin" />
        </CardContent>
      </Card>
    );
  }

  // No test taken yet
  if (!traits) {
    return (
      <Card className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Brain className="h-6 w-6 mr-3" />
            Personality Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <Brain className="h-20 w-20 mx-auto text-purple-300 mb-4" />
          <h3 className="text-2xl font-semibold text-white mb-3">
            Discover Your Personality
          </h3>
          <p className="text-white/80 mb-6 max-w-md mx-auto">
            Take our scientifically-validated 15-question assessment based on the Big Five personality model. 
            Get matched with more compatible people based on personality compatibility scores.
          </p>
          <Button
            onClick={() => setShowTest(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            size="lg"
          >
            <Brain className="h-5 w-5 mr-2" />
            Take Personality Test
          </Button>
          <p className="text-white/60 text-sm mt-4">
            ⏱️ Takes about 3 minutes
          </p>
        </CardContent>
      </Card>
    );
  }

  // Display results
  return (
    <Card className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 border-white/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center">
            <Star className="h-6 w-6 mr-3" />
            Your Personality Profile
          </CardTitle>
          <Button
            onClick={() => setShowTest(true)}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retake Test
          </Button>
        </div>
        <p className="text-white/70 text-sm mt-2">
          Based on the Big Five personality model
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {Object.entries(traits).map(([trait, score]) => {
          const info = traitDescriptions[trait];
          if (!info) return null;
          
          const displayScore = info.inverted ? 100 - score : score;
          
          return (
            <div key={trait} className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{info.icon}</span>
                  <div>
                    <h4 className="font-semibold text-white">{info.label}</h4>
                    <p className="text-sm text-white/60">{info.desc}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-3xl font-bold text-white">{displayScore}%</span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="relative w-full h-3 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`absolute left-0 top-0 h-full bg-gradient-to-r ${info.color} transition-all duration-1000`}
                  style={{ width: `${displayScore}%` }}
                />
              </div>
            </div>
          );
        })}

        <div className="bg-white/5 rounded-lg p-4 mt-6">
          <p className="text-white/70 text-sm">
            ✨ <strong>Active Matching:</strong> Your personality profile is now being used to find more compatible matches! 
            Users who also completed the test will see personality compatibility scores with you.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalityProfile;
